import React, { useEffect, useState } from "react";
import { Plug, Copy, Check, Trash2, Activity, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import { Button, Field, Spinner } from "./ui/kit";
import api, { apiError } from "../lib/api";
import { fmtDateTime } from "../lib/format";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// Převzato z Proyekty (Nastavení → Propojení), tam je konektor v provozu.
// Adresu konektoru vydává backend z MCP_PUBLIC_BASE_URL (Cloudflare Worker).
// Na hlavní doméně ho Claude odmítne — frontend tam odpovídá na OAuth
// discovery cesty stránkou 200 a Claude pak hledá přihlašovací službu.
export default function McpConnector() {
  const [tokens, setTokens] = useState([]);
  const [name, setName] = useState("Claude konektor");
  const [busy, setBusy] = useState(false);
  const [fresh, setFresh] = useState(null);          // { url, verejna } — zobrazí se jen jednou
  const [copied, setCopied] = useState(false);
  const [testUrl, setTestUrl] = useState("");
  const [testing, setTesting] = useState(false);
  const [vysledek, setVysledek] = useState(null);    // { ok, msg }
  const [chyba, setChyba] = useState("");

  const load = () => api.get("/api/mcp-tokens").then((r) => setTokens(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const vytvorit = async () => {
    setBusy(true); setChyba("");
    try {
      const { data } = await api.post("/api/mcp-tokens", { name });
      const url = data.url || `${BACKEND_URL}/api/mcp/${data.token}`;
      setFresh({ url, verejna: data.verejna_domena });
      setTestUrl(url); setVysledek(null);
      setName("Claude konektor");
      load();
    } catch (e) {
      setChyba(apiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const zrusit = async (id) => {
    if (!window.confirm("Zneplatnit tento konektor? Claude i ChatGPT s ním ztratí přístup.")) return;
    try {
      await api.delete(`/api/mcp-tokens/${id}`);
      load();
    } catch (e) { setChyba(apiError(e.response?.data?.detail)); }
  };

  const kopirovat = () => {
    navigator.clipboard.writeText(fresh.url);
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  // Test = přesně to, co udělá Claude při přidání: JSON-RPC initialize.
  // Přes fetch, ne přes `api` — interceptor by případnou 401 bral jako
  // vypršené přihlášení. Z vloženého textu se bere jen token a posílá se vždy
  // na vlastní backend, takže test nikdy neodejde na cizí doménu.
  const rpc = (token, method, params) =>
    fetch(`${BACKEND_URL}/api/mcp/${token}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ jsonrpc: "2.0", id: 1, method, params: params || {} }),
    });

  const otestovat = async () => {
    const token = (testUrl.match(/pk_[A-Za-z0-9_-]+/) || [])[0];
    if (!token) {
      setVysledek({ ok: false, msg: "V adrese chybí token konektoru (začíná „pk_“). Vlož celou adresu." });
      return;
    }
    setTesting(true); setVysledek(null);
    try {
      const res = await rpc(token, "initialize", {
        protocolVersion: "2024-11-05", capabilities: {}, clientInfo: { name: "ProfitPilot", version: "1.0" } });
      const data = await res.json().catch(() => null);
      if (data?.error) { setVysledek({ ok: false, msg: data.error.message }); return; }
      if (!res.ok || !data?.result) {
        setVysledek({ ok: false, msg: `Server odpověděl chybou (HTTP ${res.status}).` });
        return;
      }
      let who = null;
      try {
        const r2 = await rpc(token, "tools/call", { name: "whoami", arguments: {} });
        who = JSON.parse((await r2.json()).result.content[0].text);
      } catch { /* jméno je bonus — hlavní je, že handshake prošel */ }
      setVysledek({ ok: true, msg: who
        ? `Spojení funguje. AI uvidí data jako ${who.name} (${(who.role || []).join(", ")}), firma ${who.company}.`
        : "Spojení funguje. Konektor můžeš přidat do Claude nebo ChatGPT." });
    } catch {
      setVysledek({ ok: false, msg: "Server neodpověděl. Zkontroluj připojení a zkus to znovu." });
    } finally { setTesting(false); }
  };

  return (
    <div className="card p-6 mt-4" data-testid="mcp-connector">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 rounded-xl border border-line text-accent grid place-items-center shrink-0">
          <Plug size={18} strokeWidth={2} />
        </div>
        <div className="min-w-0">
          <h2 className="t-sekce font-700 text-ink">Propojení s AI (Claude, ChatGPT)</h2>
          <p className="t-meta text-muted mt-0.5">
            Zeptej se na případy, nabídky, kalendář nebo servis — nebo nech AI naplánovat výjezd a napsat servisní zápis.
            Pracuje pod tvým účtem a vidí jen to, co vidíš ty.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
        <div className="rounded-xl border border-line p-3 t-meta text-sec space-y-1">
          <div className="font-700 text-ink">Claude</div>
          <p>1. Settings → Connectors → <b>Add custom connector</b>.</p>
          <p>2. Vlož adresu konektoru, přihlašování nech prázdné, ulož.</p>
        </div>
        <div className="rounded-xl border border-line p-3 t-meta text-sec space-y-1">
          <div className="font-700 text-ink">ChatGPT</div>
          <p>1. Settings → Apps &amp; Connectors → Advanced → zapni <b>Developer mode</b>.</p>
          <p>2. Create → vlož adresu konektoru, Authentication: <b>No authentication</b>.</p>
        </div>
      </div>

      {fresh && (
        <div className="rounded-xl border border-accent p-3 mb-4 space-y-2" data-testid="mcp-fresh">
          <div className="label-up text-accent flex items-center gap-1"><Plug size={13} /> Adresa konektoru — zobrazí se jen teď</div>
          <div className="flex gap-2">
            <input readOnly value={fresh.url} className="field amount min-w-0 flex-1" data-testid="mcp-url"
              onFocus={(e) => e.target.select()} />
            <Button type="button" variant="ghost" onClick={kopirovat} className="tap shrink-0" data-testid="mcp-copy"
              aria-label="Zkopírovat adresu">
              {copied ? <Check size={16} /> : <Copy size={16} />}
            </Button>
          </div>
          {!fresh.verejna && (
            <p className="t-meta text-danger flex items-start gap-1.5">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              Na serveru chybí MCP_PUBLIC_BASE_URL — adresa vede na hlavní doménu a Claude ji nejspíš odmítne.
              Dej vědět správci.
            </p>
          )}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
        <Field label="Název konektoru" className="flex-1">
          <input className="field" value={name} onChange={(e) => setName(e.target.value)} data-testid="mcp-name" />
        </Field>
        <Button type="button" onClick={vytvorit} disabled={busy} className="tap w-full sm:w-auto" data-testid="mcp-create">
          {busy ? <Spinner /> : <><Plug size={16} /> Vytvořit konektor</>}
        </Button>
      </div>
      {chyba && <div className="t-meta text-danger mt-3">{chyba}</div>}

      <div className="border-t border-line mt-5 pt-4 space-y-2" data-testid="mcp-test">
        <span className="label-up text-muted block">Otestovat spojení</span>
        <p className="t-meta text-muted">Vlož adresu, kterou máš v Claude nebo ChatGPT, a ověř, že funguje.</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input value={testUrl} onChange={(e) => { setTestUrl(e.target.value); setVysledek(null); }}
            placeholder="https://…/api/mcp/pk_…" className="field amount min-w-0 flex-1" data-testid="mcp-test-input" />
          <Button type="button" variant="ghost" onClick={otestovat} disabled={testing || !testUrl.trim()}
            className="tap w-full sm:w-auto" data-testid="mcp-test-btn">
            {testing ? <Spinner /> : <><Activity size={16} /> Otestovat</>}
          </Button>
        </div>
        {vysledek && (
          <div data-testid="mcp-test-result"
            className={`flex items-start gap-2 t-telo rounded-xl border p-3 ${vysledek.ok ? "border-success text-success" : "border-danger text-danger"}`}>
            {vysledek.ok ? <CheckCircle2 size={16} className="shrink-0 mt-0.5" /> : <XCircle size={16} className="shrink-0 mt-0.5" />}
            <span className="min-w-0 break-words">{vysledek.msg}</span>
          </div>
        )}
      </div>

      {tokens.length > 0 && (
        <div className="border-t border-line mt-5 divide-y divide-line" data-testid="mcp-list">
          {tokens.map((t) => (
            <div key={t.id} className="py-2.5 flex items-center justify-between gap-3" data-testid={`mcp-token-${t.id}`}>
              <div className="min-w-0">
                <div className="t-telo font-600 text-ink truncate">{t.name}</div>
                <div className="t-meta text-muted">
                  Vytvořeno {fmtDateTime(t.created_at)}
                  {t.last_used_at ? ` · naposledy použito ${fmtDateTime(t.last_used_at)}` : " · zatím nepoužito"}
                </div>
              </div>
              <button type="button" onClick={() => zrusit(t.id)} aria-label="Zneplatnit konektor"
                className="tap shrink-0 rounded-full flex items-center justify-center text-muted hover:text-danger"
                data-testid={`mcp-revoke-${t.id}`}>
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
